/**
 * GDPR-compliant YouTube embedding with Usercentrics consent management
 *
 * CONFIGURATION:
 * Set Usercentrics Service ID or Template ID:
 * window.YT_UC_SERVICE_ID = "BJz7qNsdj-7";
 *
 * Or use service name fallback:
 * window.YT_UC_SERVICE_NAME = "YouTube";
 *
 * TEST CHECKLIST (before consent):
 * - Network tab: ZERO requests to youtube.com, youtube-nocookie.com, ytimg.com, googlevideo.com, i.ytimg.com, gstatic.com
 * - No iframes in DOM for YouTube videos
 * - Only placeholder visible
 *
 * TEST CHECKLIST (after consent):
 * - Network requests to YouTube domains appear
 * - Iframes are created and inserted
 * - Videos play correctly
 */

(function () {
  "use strict";

  // Configuration
  const UC_SERVICE_ID = window.YT_UC_SERVICE_ID || null;
  const UC_SERVICE_NAME = window.YT_UC_SERVICE_NAME || "YouTube";

  // YouTube domain patterns to detect
  const YT_DOMAINS = [
    "youtube.com",
    "youtube-nocookie.com",
    "youtu.be",
    "ytimg.com",
    "googlevideo.com",
    "i.ytimg.com",
    "gstatic.com",
  ];

  /**
   * Usercentrics API helper
   */
  const Usercentrics = {
    /**
     * Check if Usercentrics is available
     */
    isAvailable() {
      return (
        typeof window.UC_UI !== "undefined" ||
        typeof window.__ucCmp !== "undefined" ||
        typeof window.Usercentrics !== "undefined"
      );
    },

    /**
     * Check if Usercentrics is initialized
     */
    isInitialized() {
      if (typeof window.UC_UI !== "undefined" && window.UC_UI.isInitialized) {
        return true;
      }
      if (
        typeof window.__ucCmp !== "undefined" &&
        window.__ucCmp.isInitialized
      ) {
        return true;
      }
      return false;
    },

    /**
     * Get consent state for YouTube service
     * @returns {'accepted' | 'denied' | 'unknown'}
     */
    getConsentState() {
      if (!this.isAvailable()) {
        return "unknown";
      }

      try {
        // Try UC_UI API (v2)
        if (typeof window.UC_UI !== "undefined" && window.UC_UI.isInitialized) {
          let service = null;

          // Try by ID first
          if (UC_SERVICE_ID) {
            if (window.UC_UI.getServicesBaseInfo) {
              const services = window.UC_UI.getServicesBaseInfo();
              service = services.find(
                (s) =>
                  s.templateId === UC_SERVICE_ID ||
                  s.id === UC_SERVICE_ID ||
                  s.serviceId === UC_SERVICE_ID
              );
            } else if (window.UC_UI.getCmpData) {
              const cmpData = window.UC_UI.getCmpData();
              if (cmpData && cmpData.services) {
                service = cmpData.services.find(
                  (s) =>
                    s.templateId === UC_SERVICE_ID ||
                    s.id === UC_SERVICE_ID ||
                    s.serviceId === UC_SERVICE_ID
                );
              }
            }
          }

          // Fallback to name search
          if (!service && UC_SERVICE_NAME) {
            if (window.UC_UI.getServicesBaseInfo) {
              const services = window.UC_UI.getServicesBaseInfo();
              service = services.find(
                (s) =>
                  s.name &&
                  s.name.toLowerCase().includes(UC_SERVICE_NAME.toLowerCase())
              );
            } else if (window.UC_UI.getCmpData) {
              const cmpData = window.UC_UI.getCmpData();
              if (cmpData && cmpData.services) {
                service = cmpData.services.find(
                  (s) =>
                    s.name &&
                    s.name.toLowerCase().includes(UC_SERVICE_NAME.toLowerCase())
                );
              }
            }
          }

          if (service) {
            const status = service.consent?.status;
            if (status === true) return "accepted";
            if (status === false) return "denied";
          }
        }

        // Try __ucCmp API (v1) - defensive check
        if (
          typeof window.__ucCmp !== "undefined" &&
          window.__ucCmp.isInitialized
        ) {
          try {
            if (typeof window.__ucCmp.getConsent === "function") {
              const consent = window.__ucCmp.getConsent();
              if (consent && consent[UC_SERVICE_ID]) {
                return consent[UC_SERVICE_ID] ? "accepted" : "denied";
              }
            }
          } catch (e) {
            // Silently fail - UC API not available or different version
          }
        }
      } catch (e) {
        // Silently fail - UC not initialized or API error
        // Don't log to avoid console spam
      }

      return "unknown";
    },

    /**
     * Check if YouTube is allowed
     * @returns {boolean | null} - true if allowed, false if denied, null if unknown
     */
    isYouTubeAllowed() {
      const state = this.getConsentState();
      if (state === "accepted") return true;
      if (state === "denied") return false;
      return null;
    },

    /**
     * Open Usercentrics consent dialog
     */
    openDialog() {
      if (!this.isAvailable()) {
        console.warn("[YouTubeConsent] Usercentrics not available");
        return;
      }

      try {
        // Try UC_UI API (v2)
        if (typeof window.UC_UI !== "undefined" && window.UC_UI.isInitialized) {
          if (UC_SERVICE_ID && window.UC_UI.showSecondLayer) {
            window.UC_UI.showSecondLayer(UC_SERVICE_ID);
            return;
          }
          if (window.UC_UI.showFirstLayer) {
            window.UC_UI.showFirstLayer();
            return;
          }
        }

        // Try __ucCmp API (v1)
        if (
          typeof window.__ucCmp !== "undefined" &&
          window.__ucCmp.isInitialized
        ) {
          if (window.__ucCmp.showFirstLayer) {
            window.__ucCmp.showFirstLayer();
            return;
          }
        }

        // Fallback: try to trigger UC banner
        if (typeof window.UC_UI !== "undefined" && window.UC_UI.show) {
          window.UC_UI.show();
        }
      } catch (e) {
        console.warn("[YouTubeConsent] Error opening dialog:", e);
      }
    },

    /**
     * Subscribe to consent changes
     * @param {Function} callback - Called when consent changes
     */
    subscribeToChanges(callback) {
      if (!this.isAvailable()) return;

      // UC_UI events (v2)
      if (typeof window.addEventListener !== "undefined") {
        window.addEventListener("UC_UI_CONSENT_GIVEN", callback);
        window.addEventListener("UC_UI_CONSENT_UPDATED", callback);
        window.addEventListener("UC_UI_CONSENT_DENIED", callback);
      }

      // Polling fallback (light polling for 15 seconds after dialog might open)
      let pollCount = 0;
      const maxPolls = 30; // 15 seconds at 500ms intervals
      const pollInterval = setInterval(() => {
        pollCount++;
        callback();
        if (pollCount >= maxPolls) {
          clearInterval(pollInterval);
        }
      }, 500);
    },
  };

  /**
   * Extract YouTube video ID from various URL formats
   * @param {string} url - YouTube URL
   * @returns {string|null} - Video ID or null
   */
  function extractVideoId(url) {
    if (!url) return null;

    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/.*[?&]v=([^&\n?#]+)/,
      /youtu\.be\/([^?\n#]+)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }

    return null;
  }

  /**
   * Check if URL is a YouTube domain
   * @param {string} url - URL to check
   * @returns {boolean}
   */
  function isYouTubeDomain(url) {
    if (!url) return false;
    return YT_DOMAINS.some((domain) => url.includes(domain));
  }

  /**
   * YouTube Consent Embed Controller
   */
  class YouTubeConsentEmbed {
    constructor(element) {
      this.element = element;
      this.videoId = element.dataset.videoId;
      this.title = element.dataset.title || "YouTube video";
      this.ratio = parseFloat(element.dataset.ratio) || 1.7777;
      this.loaded = false;

      this.init();
    }

    /**
     * Ensure the embed is actually interactive inside css-slider.
     *
     * Some theme slider logic (see assets/component-slider.js) uses `inert` to
     * disable interaction on slides it believes are inactive. When that state
     * desyncs, the visually active slide can still be inert, causing "click does nothing".
     *
     * We scope the fix to the slide that contains this YouTube embed only.
     */
    ensureInteractiveInSlider() {
      const slide =
        this.element && this.element.closest
          ? this.element.closest(".product-gallery-item")
          : null;

      if (!slide) return;

      const removeInertIfPresent = () => {
        if (slide.hasAttribute("inert")) {
          slide.removeAttribute("inert");
        }
      };

      // Immediate attempt
      removeInertIfPresent();

      // Keep it interactive if the slider re-applies inert
      if (!this._inertObserver) {
        this._inertObserver = new MutationObserver(() => {
          removeInertIfPresent();
        });
        this._inertObserver.observe(slide, {
          attributes: true,
          attributeFilter: ["inert"],
        });
      }
    }

    init() {
      // Make sure the element is clickable in slider contexts (even if slider state desyncs)
      this.ensureInteractiveInSlider();

      // Slide-level fallback:
      // If some overlay element inside the same slide captures the click (so our button/wrapper
      // never becomes the event target), we still want "Video laden" to work.
      // We listen on the containing slide in CAPTURE phase and trigger load only when the
      // pointer/click is inside the actual button's bounding box.
      this.bindSlideLevelFallback();

      // Use event delegation on the embed wrapper - more reliable than button-level handlers
      // This ensures clicks are caught even if the button is replaced or overlapped
      const wrapper = this.element.querySelector(
        ".youtube-consent-embed__wrapper"
      );

      if (!wrapper) {
        console.warn("[YouTubeConsent] Wrapper not found!", this.element);
        return;
      }

      // Primary handler: event delegation on wrapper in capture phase
      // This runs BEFORE any slider/card handlers can intercept
      const wrapperClickHandler = (e) => {
        const target = e.target;
        const clickedButton =
          target.closest && target.closest("[data-yt-load-button]");

        if (clickedButton) {
          console.log(
            "[YouTubeConsent] Wrapper delegation caught button click!",
            {
              videoId: this.videoId,
              target: target,
              clickedButton: clickedButton,
            }
          );
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.handleLoadClick();
          return false;
        }
      };

      // Add to wrapper in capture phase (runs first)
      wrapper.addEventListener("click", wrapperClickHandler, true);
      wrapper.addEventListener("mousedown", wrapperClickHandler, true);
      this._wrapperClickHandler = wrapperClickHandler;

      // Also bind directly to button as backup
      const loadButton = this.element.querySelector("[data-yt-load-button]");
      if (loadButton) {
        loadButton.addEventListener(
          "click",
          (e) => {
            console.log("[YouTubeConsent] Button direct click handler", {
              videoId: this.videoId,
            });
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.handleLoadClick();
            return false;
          },
          true
        );
      }

      // Add document-level listener in capture phase to catch clicks before anything else intercepts them
      // This is a workaround for cases where other scripts (like autoblocker) might intercept clicks
      const documentClickHandler = (e) => {
        const target = e.target;
        const clickedButton =
          target.closest && target.closest("[data-yt-load-button]");
        const closestEmbed =
          target.closest && target.closest(".youtube-consent-embed");

        // Check if click is on button within our embed
        if (closestEmbed === this.element && clickedButton) {
          console.log(
            "[YouTubeConsent] Document-level handler caught button click!",
            {
              videoId: this.videoId,
              target: target,
            }
          );
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.handleLoadClick();
          return false;
        }
      };

      // Use capture phase and add to document to catch before any other handlers
      document.addEventListener("click", documentClickHandler, true);
      this._documentClickHandler = documentClickHandler;

      // Global click handler - final fallback to catch button clicks
      // This runs at window level in capture phase to catch clicks before anything else
      const globalClickHandler = (e) => {
        const target = e.target;
        const clickedButton =
          target.closest && target.closest("[data-yt-load-button]");
        const closestEmbed =
          target.closest && target.closest(".youtube-consent-embed");

        // Only handle clicks within our embed
        if (closestEmbed === this.element && clickedButton) {
          console.log("[YouTubeConsent] Global handler triggered!", {
            target: target,
            clickedButton: clickedButton,
            videoId: this.videoId,
          });
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.handleLoadClick();
          return false;
        }
      };
      window.addEventListener("click", globalClickHandler, true);
      window.addEventListener("mousedown", globalClickHandler, true);
      this._globalClickHandler = globalClickHandler;

      // Prevent slider from intercepting clicks within YouTube embed area
      // The slider's mousedown handler adds 'can-drag' class which interferes with clicks
      const preventSliderIntercept = (e) => {
        const target = e.target;
        // Check if click is anywhere within our embed
        if (this.element.contains(target)) {
          console.log(
            "[YouTubeConsent] Preventing slider from intercepting embed click",
            {
              target: target,
              eventType: e.type,
            }
          );
          e.stopPropagation();
          e.stopImmediatePropagation();
          // Prevent slider from adding 'can-drag' class
          const sliderHolder = this.element.closest(".css-slider-holder");
          if (sliderHolder && sliderHolder.classList.contains("can-drag")) {
            sliderHolder.classList.remove("can-drag");
          }
          // Don't prevent default, let the click handler do it
        }
      };

      // Add to the slider element if it exists - use capture phase to run BEFORE slider's handler
      const sliderHolder = this.element.closest(".css-slider-holder");
      if (sliderHolder) {
        sliderHolder.addEventListener(
          "mousedown",
          preventSliderIntercept,
          true
        );
        sliderHolder.addEventListener("click", preventSliderIntercept, true);
        this._preventSliderIntercept = preventSliderIntercept;
        this._sliderHolder = sliderHolder;
      }

      // Skip auto-load - user must click button
      // Remove programmatic test - it interferes with manual clicks
    }

    handleLoadClick() {
      console.log("[YouTubeConsent] handleLoadClick called", {
        videoId: this.videoId,
        loaded: this.loaded,
      });

      // Check if actually loaded (iframe exists in DOM)
      const container = this.element.querySelector(
        "[data-yt-iframe-container]"
      );
      const existingIframe = container && container.querySelector("iframe");

      if (this.loaded && existingIframe) {
        console.log(
          "[YouTubeConsent] Already loaded and iframe exists, skipping"
        );
        return;
      }

      // If marked as loaded but iframe is missing, reset and reload
      if (this.loaded && !existingIframe) {
        console.warn(
          "[YouTubeConsent] Marked as loaded but iframe missing! Resetting and reloading..."
        );
        this.loaded = false;
        this.element.removeAttribute("data-loaded");
        if (container) {
          container.hidden = true;
        }
        const placeholder = this.element.querySelector(
          ".youtube-consent-embed__placeholder"
        );
        if (placeholder) {
          placeholder.removeAttribute("hidden");
          placeholder.style.display = "";
        }
      }

      this.loadEmbed();
    }

    loadEmbed() {
      console.log("[YouTubeConsent] loadEmbed called", {
        loaded: this.loaded,
        videoId: this.videoId,
        element: this.element,
      });

      if (this.loaded) {
        console.log("[YouTubeConsent] Already loaded, skipping");
        return;
      }

      if (!this.videoId) {
        console.error("[YouTubeConsent] No video ID!");
        return;
      }

      const container = this.element.querySelector(
        "[data-yt-iframe-container]"
      );
      const placeholder = this.element.querySelector(
        ".youtube-consent-embed__placeholder"
      );

      console.log("[YouTubeConsent] Elements found", {
        container: !!container,
        placeholder: !!placeholder,
        containerHidden: container ? container.hidden : "N/A",
      });

      if (!container) {
        console.error("[YouTubeConsent] Container not found!");
        return;
      }

      // Create iframe
      console.log("[YouTubeConsent] Creating iframe for video:", this.videoId);
      const iframe = document.createElement("iframe");
      
      // Check if we're in a video-component context
      const isVideoComponent = this.element.closest('.video-component') !== null;
      const context = this.element.dataset.context || '';
      const videoComponent = isVideoComponent ? this.element.closest('.video-component') : null;
      const wrapper = this.element.closest('.video-component__youtube-consent-wrapper');
      
      // Get video-component settings from wrapper data attributes
      const shouldAutoplay = videoComponent && videoComponent.dataset.autoplay === 'true';
      const shouldLoop = wrapper && wrapper.dataset.videoComponentLoop === 'true';
      const mutedParam = wrapper ? wrapper.dataset.videoComponentMuted : '';
      const controlsParam = wrapper ? wrapper.dataset.videoComponentControls : '';
      const loopParam = wrapper && shouldLoop ? wrapper.dataset.videoComponentLoopParam : '';
      
      // Build iframe src with proper parameters
      let iframeSrc = `https://www.youtube-nocookie.com/embed/${this.videoId}?rel=0&modestbranding=1`;
      
      // Add parameters for video-component compatibility
      if (isVideoComponent || context === 'video-component') {
        iframeSrc += '&enablejsapi=1&playsinline=1';
        
        // Add autoplay, mute, and controls based on video-component settings
        if (shouldAutoplay) {
          iframeSrc += '&autoplay=1';
          if (mutedParam) iframeSrc += mutedParam;
          if (controlsParam) iframeSrc += controlsParam;
        } else {
          iframeSrc += '&autoplay=0';
          if (controlsParam) iframeSrc += controlsParam;
        }
        
        // Add loop parameter if needed
        if (shouldLoop && loopParam) {
          iframeSrc += loopParam;
        }
      } else {
        iframeSrc += '&autoplay=0';
      }
      
      iframe.src = iframeSrc;
      iframe.setAttribute("loading", "lazy");
      iframe.setAttribute("title", this.title);
      iframe.setAttribute(
        "allow",
        "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      );
      iframe.setAttribute("allowfullscreen", "");
      iframe.setAttribute("referrerpolicy", "no-referrer-when-downgrade");
      
      // Add classes for video-component compatibility
      if (isVideoComponent || context === 'video-component') {
        iframe.className = "youtube-consent-embed__iframe js-youtube video-component__iframe";
      } else {
        iframe.className = "youtube-consent-embed__iframe";
      }

      // Hide placeholder, show container
      if (placeholder) {
        console.log("[YouTubeConsent] Hiding placeholder");
        placeholder.setAttribute("hidden", "");
        placeholder.style.display = "none";
      }

      console.log("[YouTubeConsent] Showing container and appending iframe");
      container.removeAttribute("hidden");
      container.style.display = "block";
      container.style.visibility = "visible";
      container.appendChild(iframe);

      this.loaded = true;
      this.element.dataset.loaded = "true";

      // Verify iframe was actually added and persists
      setTimeout(() => {
        const addedIframe = container.querySelector("iframe");
        const containerStillVisible =
          !container.hidden &&
          window.getComputedStyle(container).display !== "none";

        console.log("[YouTubeConsent] Verification after 100ms:", {
          iframeSrc: iframe.src,
          containerHidden: container.hidden,
          containerDisplay: window.getComputedStyle(container).display,
          containerChildren: container.children.length,
          iframeInDOM: !!addedIframe,
          iframeSrcInDOM: addedIframe ? addedIframe.src : "N/A",
          containerStillVisible: containerStillVisible,
          placeholderHidden: placeholder ? placeholder.hidden : "N/A",
          placeholderDisplay: placeholder
            ? window.getComputedStyle(placeholder).display
            : "N/A",
        });

        // If iframe was removed, re-add it
        if (!addedIframe && container.children.length === 0) {
          console.warn("[YouTubeConsent] Iframe was removed! Re-adding...");
          container.appendChild(iframe);
          container.removeAttribute("hidden");
          container.style.display = "block";
          container.style.visibility = "visible";
        }

        // If container was hidden again, show it
        if (
          container.hidden ||
          window.getComputedStyle(container).display === "none"
        ) {
          console.warn(
            "[YouTubeConsent] Container was hidden! Showing again..."
          );
          container.removeAttribute("hidden");
          container.style.display = "block";
          container.style.visibility = "visible";
        }
      }, 100);

      console.log("[YouTubeConsent] Embed loaded successfully!", {
        iframeSrc: iframe.src,
        containerHidden: container.hidden,
        containerChildren: container.children.length,
      });
    }

    unloadEmbed() {
      const container = this.element.querySelector(
        "[data-yt-iframe-container]"
      );
      const placeholder = this.element.querySelector(
        ".youtube-consent-embed__placeholder"
      );

      if (container) {
        const iframe = container.querySelector("iframe");
        if (iframe) {
          iframe.remove();
        }
        container.hidden = true;
      }

      if (placeholder) {
        placeholder.hidden = false;
      }

      this.loaded = false;
      this.element.removeAttribute("data-loaded");
    }

    /**
     * Bind capture listeners on the containing slide so clicks still work even if
     * another element overlays the button (hit-testing) but lives in the same slide.
     */
    bindSlideLevelFallback() {
      const slide =
        this.element && this.element.closest
          ? this.element.closest(".product-gallery-item")
          : null;

      if (!slide || this._slideFallbackBound) return;

      const shouldTriggerFromEvent = (e) => {
        // Only react to primary button/taps
        if (e && "button" in e && e.button !== 0) return false;

        const btn = this.element.querySelector("[data-yt-load-button]");
        if (!btn) return false;

        const rect = btn.getBoundingClientRect();
        if (!rect || rect.width === 0 || rect.height === 0) return false;

        const x = e.clientX;
        const y = e.clientY;
        if (typeof x !== "number" || typeof y !== "number") return false;

        return (
          x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
        );
      };

      const handler = (e) => {
        if (!shouldTriggerFromEvent(e)) return;

        // Prevent slider / overlays from hijacking the interaction
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        this.handleLoadClick();
        return false;
      };

      // pointerup covers mouse + touch in modern browsers; click is a safety net
      slide.addEventListener("pointerup", handler, true);
      slide.addEventListener("click", handler, true);

      this._slideFallbackBound = true;
      this._slideFallbackSlide = slide;
      this._slideFallbackHandler = handler;
    }

    destroy() {
      // Clean up all listeners
      const wrapper = this.element.querySelector(
        ".youtube-consent-embed__wrapper"
      );
      if (wrapper && this._wrapperClickHandler) {
        wrapper.removeEventListener("click", this._wrapperClickHandler, true);
        wrapper.removeEventListener(
          "mousedown",
          this._wrapperClickHandler,
          true
        );
      }

      if (this._documentClickHandler) {
        document.removeEventListener("click", this._documentClickHandler, true);
      }

      if (this._globalClickHandler) {
        window.removeEventListener("click", this._globalClickHandler, true);
        window.removeEventListener("mousedown", this._globalClickHandler, true);
      }

      if (this._sliderHolder && this._preventSliderIntercept) {
        this._sliderHolder.removeEventListener(
          "mousedown",
          this._preventSliderIntercept,
          true
        );
        this._sliderHolder.removeEventListener(
          "click",
          this._preventSliderIntercept,
          true
        );
      }

      if (this._inertObserver) {
        this._inertObserver.disconnect();
        this._inertObserver = null;
      }

      if (
        this._slideFallbackBound &&
        this._slideFallbackSlide &&
        this._slideFallbackHandler
      ) {
        this._slideFallbackSlide.removeEventListener(
          "pointerup",
          this._slideFallbackHandler,
          true
        );
        this._slideFallbackSlide.removeEventListener(
          "click",
          this._slideFallbackHandler,
          true
        );
      }
      this._slideFallbackBound = false;
      this._slideFallbackSlide = null;
      this._slideFallbackHandler = null;
    }
  }

  /**
   * Initialize all YouTube consent embeds
   */
  function initEmbeds() {
    const embeds = document.querySelectorAll('[data-yt-consent-embed="true"]');

    embeds.forEach((embed) => {
      if (!embed.dataset.initialized && !embed._ytController) {
        embed.dataset.initialized = "true";
        embed._ytController = new YouTubeConsentEmbed(embed);
      }
    });
  }

  /**
   * Global (capture) delegation fallback.
   *
   * In some storefront environments, click events on the button never reach the
   * per-embed handlers (due to other scripts/overlays/slider logic). This is a
   * final safety net: if a [data-yt-load-button] interaction happens anywhere,
   * we ensure a controller exists and call handleLoadClick().
   *
   * Scoped: only reacts to elements within .youtube-consent-embed.
   */
  function initGlobalButtonDelegation() {
    if (window.__ytConsentGlobalDelegationInitialized) return;
    window.__ytConsentGlobalDelegationInitialized = true;

    const handler = (e) => {
      const target = e.target;
      const btn =
        target && target.closest
          ? target.closest("[data-yt-load-button]")
          : null;
      if (!btn) return;

      const embed = btn.closest ? btn.closest(".youtube-consent-embed") : null;
      if (!embed) return;

      // De-dupe: pointerdown + click/pointerup can both fire.
      const now = Date.now();
      if (embed.__ytLastTriggerAt && now - embed.__ytLastTriggerAt < 600)
        return;
      embed.__ytLastTriggerAt = now;
      embed.dataset.ytTriggerAt = String(now);

      // Ensure controller exists (and interactive)
      if (!embed._ytController) {
        try {
          embed.dataset.initialized = "true";
          embed._ytController = new YouTubeConsentEmbed(embed);
        } catch (err) {
          // If instantiation fails for any reason, allow retries later
          try {
            embed.removeAttribute("data-initialized");
            delete embed.dataset.initialized;
          } catch (_) {}
          console.warn(
            "[YouTubeConsent] Failed to initialize controller from global handler",
            err
          );
          return;
        }
      } else if (
        typeof embed._ytController.ensureInteractiveInSlider === "function"
      ) {
        embed._ytController.ensureInteractiveInSlider();
      }

      // Prevent other handlers from hijacking the click after we take action
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      embed._ytController.handleLoadClick();
      return false;
    };

    // Attach as early as possible (window capture). Some scripts cancel click/pointerup;
    // pointerdown/touchstart usually still gets through.
    window.addEventListener("pointerdown", handler, true);
    window.addEventListener("touchstart", handler, true);
    window.addEventListener("mousedown", handler, true);
    window.addEventListener("pointerup", handler, true);
    window.addEventListener("click", handler, true);
  }

  /**
   * Handle consent changes - auto-load if consent given
   */
  function handleConsentChange() {
    const state = Usercentrics.getConsentState();
    const embeds = document.querySelectorAll('[data-yt-consent-embed="true"]');

    embeds.forEach((embed) => {
      const controller = embed._ytController;
      if (controller && state === "accepted" && !controller.loaded) {
        controller.loadEmbed();
      }
    });
  }

  /**
   * MutationObserver to catch raw YouTube iframes - simplified: just normalize URLs
   */
  function initMutationObserver() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            // Element node
            // Check if node is an iframe
            if (node.tagName === "IFRAME" && isYouTubeDomain(node.src)) {
              // Normalize to nocookie domain
              if (
                node.src.includes("youtube.com/embed") &&
                !node.src.includes("youtube-nocookie.com")
              ) {
                node.src = node.src.replace(
                  "youtube.com/embed",
                  "youtube-nocookie.com/embed"
                );
              }
            }

            // Check children for iframes
            const iframes =
              node.querySelectorAll && node.querySelectorAll("iframe");
            if (iframes) {
              iframes.forEach((iframe) => {
                if (isYouTubeDomain(iframe.src)) {
                  // Normalize to nocookie domain
                  if (
                    iframe.src.includes("youtube.com/embed") &&
                    !iframe.src.includes("youtube-nocookie.com")
                  ) {
                    iframe.src = iframe.src.replace(
                      "youtube.com/embed",
                      "youtube-nocookie.com/embed"
                    );
                  }
                }
              });
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Create placeholder from iframe
   */
  function createPlaceholderFromIframe(iframe, videoId) {
    const wrapper = document.createElement("div");
    wrapper.className = "youtube-consent-embed";
    wrapper.setAttribute("data-yt-consent-embed", "true");
    wrapper.setAttribute("data-video-id", videoId);
    wrapper.style.cssText = iframe.style.cssText || "";

    // Copy aspect ratio if available
    const computedStyle = window.getComputedStyle(iframe);
    if (computedStyle.width && computedStyle.height) {
      const width = parseFloat(computedStyle.width);
      const height = parseFloat(computedStyle.height);
      if (width && height) {
        wrapper.setAttribute("data-ratio", (width / height).toFixed(4));
      }
    }

    // Create placeholder HTML (simplified version - no privacy button)
    wrapper.innerHTML = `
      <div class="youtube-consent-embed__wrapper">
        <div class="youtube-consent-embed__placeholder">
          <div class="youtube-consent-embed__poster" aria-hidden="true">
            <svg width="100%" height="100%" viewBox="0 0 120 68" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="120" height="68" fill="#1a1a1a"/>
              <path d="M48 25L48 43L60 34L48 25Z" fill="#fff" fill-opacity="0.9"/>
            </svg>
          </div>
          <div class="youtube-consent-embed__content">
            <h3 class="youtube-consent-embed__headline">Externer Inhalt (YouTube)</h3>
            <p class="youtube-consent-embed__text">Dieser Inhalt wird von YouTube bereitgestellt. Durch das Laden des Videos erlauben Sie die Übertragung von Daten an YouTube/Google und Cookies können gespeichert werden.</p>
            <div class="youtube-consent-embed__actions">
              <button type="button" class="youtube-consent-embed__button youtube-consent-embed__button--primary" data-yt-load-button>Video laden</button>
            </div>
            <div class="youtube-consent-embed__status" data-yt-status hidden>
              <p class="youtube-consent-embed__status-text" data-yt-status-text></p>
            </div>
          </div>
        </div>
        <div class="youtube-consent-embed__iframe-container" data-yt-iframe-container hidden></div>
      </div>
    `;

    // Initialize the new embed
    setTimeout(() => {
      wrapper._ytController = new YouTubeConsentEmbed(wrapper);
    }, 0);

    return wrapper;
  }

  // Initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initEmbeds();
      initGlobalButtonDelegation();
      initMutationObserver();
    });
  } else {
    initEmbeds();
    initGlobalButtonDelegation();
    initMutationObserver();
  }

  // Subscribe to consent changes
  Usercentrics.subscribeToChanges(handleConsentChange);

  // Re-check on window load (UC might initialize late)
  window.addEventListener("load", () => {
    setTimeout(() => {
      handleConsentChange();
      initEmbeds();
    }, 1000);
  });
})();
